// ── Importaciones tal cual ──────────────────────────────
import { useState, useEffect } from "react";
import { BRANCHES, MACHINES } from "../data/constants";
import { formatDate } from "../utils/date";
import { useWindowSize } from "../hooks/useWindowSize";
import RangeCalendar from "./RangeCalendar";
import { useNavigate, useParams } from "react-router-dom";
import BranchMachineAssign from "./BranchMachineAssign";
import { MachinesFetchAPI } from "../../../api/Machines";
import { getRandomColor } from "../../utils/RandomColor";
import { EventsFetchAPI } from "../../../api/Events";
import AlertSmall from "../../Alerts/AlertSmall";
import { RenderIf } from '../../utils/RenderIf';
import MomentusEventPanel from './MomentusEventPanel';

/* ─────────────────────────────────────────────────────────
   CSS — inyectado una vez. Prefijo: .evd-
───────────────────────────────────────────────────────── */
const EVD_CSS = `
@keyframes evd-up   { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
@keyframes evd-fade { from{opacity:0} to{opacity:1} }

/* ════════════════════════════════════════
   PANEL — 3 zonas fijas
════════════════════════════════════════ */
.evd-panel {
  display: flex; flex-direction: column;
  height: 100%; overflow: hidden;
  font-family: var(--font-sans,-apple-system,sans-serif);
}

/* ── zona 1: header fijo ── */
.evd-panel-header {
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; gap: .75rem;
  padding: .9rem 1rem .75rem;
  border-bottom: 1px solid rgba(0,0,0,.07);
  background: #fff;
}
.evd-panel-eyebrow {
  font-size: .62rem; font-weight: 700; letter-spacing: .09em;
  text-transform: uppercase; color: #9CA3AF; margin: 0 0 .18rem;
}
.evd-panel-title {
  font-size: .98rem; font-weight: 800; color: #0A0A0A; margin: 0;
  line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 220px;
}

/* ── zona 1b: tabs fijos ── */
.evd-panel-tabs {
  flex-shrink: 0;
  display: flex; padding: 0 .85rem;
  border-bottom: 2px solid rgba(0,0,0,.07);
  background: #fff;
}
.evd-tab {
  padding: .62rem .85rem;
  border: none; background: transparent;
  font-family: var(--font-sans,-apple-system,sans-serif);
  font-size: .79rem; font-weight: 600; color: #9CA3AF;
  cursor: pointer; display: flex; align-items: center; gap: .35rem;
  border-bottom: 2.5px solid transparent; margin-bottom: -2px;
  transition: color .14s, border-color .14s; white-space: nowrap;
}
.evd-tab:hover { color: #1B3A6B; }
.evd-tab.active { color: #1B3A6B; border-bottom-color: #1B3A6B; }
.evd-tab-badge {
  background: rgba(0,0,0,.07); color: #6B7280;
  border-radius: 999px; padding: .1rem .45rem;
  font-size: .66rem; font-weight: 700;
  transition: background .14s, color .14s;
}
.evd-tab.active .evd-tab-badge { background: #1B3A6B; color: #fff; }

/* ── zona 2: body scrollable ── */
.evd-panel-body {
  flex: 1; overflow-y: auto;
  padding: .85rem .95rem;
  display: flex; flex-direction: column; gap: .55rem;
}

/* ── zona 3: footer fijo ── */
.evd-panel-footer {
  flex-shrink: 0;
  display: flex; align-items: center; gap: .5rem;
  padding: .75rem .95rem;
  border-top: 1.5px solid rgba(0,0,0,.08);
  background: #fff;
}

/* ── close button ── */
.evd-close {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  border: 1.5px solid rgba(0,0,0,.09); background: rgba(0,0,0,.035);
  color: #6B6B6B; display: grid; place-items: center; cursor: pointer;
  transition: all .14s; font-size: 13px; line-height: 1;
}
.evd-close:hover { border-color: #EF4444; color: #EF4444; background: #FEE2E2; }

/* ════════════════════════════════════════
   INFO ROWS (tab 1, vista)
════════════════════════════════════════ */
.evd-ir {
  display: flex; align-items: center; gap: .75rem;
  padding: .7rem .9rem;
  background: #F9FAFB;
  border: 1.5px solid rgba(0,0,0,.07);
  border-radius: 12px;
  cursor: pointer; transition: border-color .14s, background .14s;
  animation: evd-up .2s ease both;
}
.evd-ir:hover { border-color: rgba(27,58,107,.28); background: rgba(27,58,107,.04); }
.evd-ir.evd-static { cursor: default; }
.evd-ir.evd-static:hover { border-color: rgba(0,0,0,.07); background: #F9FAFB; }
.evd-ir-content { flex: 1; display: flex; flex-direction: column; gap: .1rem; min-width: 0; }
.evd-ir-label {
  font-size: .61rem; font-weight: 700; letter-spacing: .08em;
  text-transform: uppercase; color: #9CA3AF;
}
.evd-ir-value {
  font-size: .87rem; font-weight: 600; color: #111827;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.evd-ir-pencil { color: #C9CFD8; flex-shrink: 0; transition: color .14s; }
.evd-ir:hover .evd-ir-pencil { color: #1B3A6B; }

/* chips inside info row */
.evd-chip-row { display: flex; flex-wrap: wrap; gap: .35rem; margin-top: .25rem; }
.evd-chip {
  display: inline-flex; align-items: center; gap: .3rem;
  padding: .25rem .65rem; border-radius: 7px;
  font-size: .71rem; font-weight: 600;
  font-family: var(--font-sans,-apple-system,sans-serif);
}
.evd-empty-hint { font-size: .72rem; color: #C0C7D0; }

/* ════════════════════════════════════════
   EDIT FORMS (tab 1, edición)
════════════════════════════════════════ */
.evd-edit-label {
  display: block; font-size: .63rem; font-weight: 700;
  letter-spacing: .08em; text-transform: uppercase; color: #9CA3AF;
  margin-bottom: .4rem;
  font-family: var(--font-sans,-apple-system,sans-serif);
}
.evd-section-label {
  display: block; font-size: .68rem; font-weight: 700;
  letter-spacing: .07em; text-transform: uppercase; color: #9CA3AF;
  font-family: var(--font-sans,-apple-system,sans-serif);
  margin-bottom: .5rem;
}
.evd-input, .evd-textarea {
  width: 100%; padding: .62rem .85rem; border-radius: 10px;
  border: 1.5px solid rgba(0,0,0,.11); background: rgba(0,0,0,.03);
  font-size: .85rem; color: #111827;
  font-family: var(--font-sans,-apple-system,sans-serif);
  transition: border-color .14s, box-shadow .14s; outline: none;
}
.evd-input:focus, .evd-textarea:focus {
  border-color: #1B3A6B; box-shadow: 0 0 0 3px rgba(27,58,107,.09); background: #fff;
}
.evd-textarea { resize: vertical; min-height: 80px; }
.evd-range-wrap {
  border: 1.5px solid rgba(0,0,0,.08); border-radius: 14px;
  padding: .75rem; background: #fff; overflow: hidden;
}

/* ════════════════════════════════════════
   BRANCH ACCORDION (tab 2)
════════════════════════════════════════ */
.evd-accordion {
  border-radius: 12px;
  transition: box-shadow .2s, background .2s;
  overflow: hidden;
  animation: evd-up .18s ease both;
}
.evd-accordion-header {
  padding: .65rem .9rem;
  font-weight: 600; font-size: .85rem;
  font-family: var(--font-sans,-apple-system,sans-serif);
  display: flex; align-items: center;
  transition: background .14s, color .14s;
}
.evd-accordion-body {
  padding: .8rem .9rem;
  background: #F9FAFB;
  display: flex; gap: .5rem; flex-wrap: wrap; flex-direction: column;
  animation: evd-fade .18s ease both;
}
.evd-machine-badge {
  display: flex; align-items: center; gap: .5rem;
  padding: .5rem .85rem; border-radius: 9px;
  font-weight: 600; font-size: .82rem;
  font-family: var(--font-sans,-apple-system,sans-serif);
}
.evd-machine-badge-del {
  background: transparent; border: none; font-size: 16px;
  cursor: pointer; line-height: 1; display: flex; align-items: center;
  opacity: .7; transition: opacity .13s, transform .13s; margin-left: auto;
}
.evd-machine-badge-del:hover { opacity: 1; transform: scale(1.15); }
.evd-machine-add {
  display: inline-flex; align-items: center; gap: .4rem;
  padding: .5rem .9rem; border-radius: 9px;
  font-weight: 600; font-size: .82rem;
  font-family: var(--font-sans,-apple-system,sans-serif);
  background: #1B3A6B; color: #fff; border: 1.5px solid #1B3A6B;
  cursor: pointer; transition: all .14s; margin-bottom: 8px;
}
.evd-machine-add:hover { background: #152D54; transform: translateY(-1px); }

/* add-branch option */
.evd-branch-option {
  display: flex; align-items: center; gap: .65rem;
  width: 100%; text-align: left; padding: .6rem .85rem; border-radius: 10px;
  border: 1.5px solid rgba(0,0,0,.08); background: #fff;
  cursor: pointer; transition: all .14s;
  font-family: var(--font-sans,-apple-system,sans-serif);
  animation: evd-up .15s ease both;
}
.evd-branch-option:hover { border-color: rgba(27,58,107,.3); }
.evd-branch-icon { width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0; display: grid; place-items: center; background: rgba(27,58,107,.1); color: #1B3A6B; }
.evd-branch-icon--inactive { background: rgba(0,0,0,.05); color: #9CA3AF; }
.evd-branch-name { font-size: .83rem; font-weight: 700; color: #111827; }
.evd-branch-name--inactive { color: #6B7280; }
.evd-branch-address { font-size: .72rem; color: #9CA3AF; margin-top: 1px; }
.evd-branch-check { margin-left: auto; color: #16A34A; font-weight: 800; font-size: 1rem; flex-shrink: 0; }

/* ════════════════════════════════════════
   BUTTONS
════════════════════════════════════════ */
.evd-btn-primary {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: .4rem;
  padding: .62rem 1.1rem; border-radius: 10px;
  background: #1B3A6B; color: #fff; border: 1.5px solid #1B3A6B;
  font-family: var(--font-sans,-apple-system,sans-serif);
  font-size: .83rem; font-weight: 700; cursor: pointer; transition: all .14s;
}
.evd-btn-primary:hover:not(:disabled) { background: #152D54; transform: translateY(-1px); }
.evd-btn-primary:disabled { opacity: .45; cursor: not-allowed; }
.evd-btn-secondary {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: .4rem;
  padding: .62rem 1.1rem; border-radius: 10px;
  background: rgba(0,0,0,.04); color: #6B7280;
  border: 1.5px solid rgba(0,0,0,.12);
  font-family: var(--font-sans,-apple-system,sans-serif);
  font-size: .83rem; font-weight: 600; cursor: pointer; transition: all .14s;
}
.evd-btn-secondary:hover { border-color: #1B3A6B; color: #1B3A6B; }
.evd-btn-danger {
  display: inline-flex; align-items: center; gap: .4rem;
  padding: .5rem 1rem; border-radius: 9px;
  background: rgba(239,68,68,.07); color: #EF4444; border: 1.5px solid rgba(239,68,68,.22);
  font-family: var(--font-sans,-apple-system,sans-serif);
  font-size: .79rem; font-weight: 600; cursor: pointer; transition: all .14s;
}
.evd-btn-danger:hover { background: #EF4444; color: #fff; border-color: #EF4444; }
.evd-pill-active   { background: rgba(22,163,74,.08) !important; color: #16A34A !important; border-color: rgba(22,163,74,.3) !important; }
.evd-pill-inactive { background: rgba(0,0,0,.04) !important; color: #9CA3AF !important; border-color: rgba(0,0,0,.12) !important; }

@media(max-width: 480px) {
  .evd-panel-footer { flex-wrap: wrap; }
  .evd-panel-title  { max-width: 180px; font-size: .9rem; }
}
`;

if (typeof window !== 'undefined' && !document.getElementById('evd-styles')) {
  const el = document.createElement('style');
  el.id = 'evd-styles'; el.textContent = EVD_CSS;
  document.head.appendChild(el);
}

/* ── ICONS ── */
const IcoEdit      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>;
const IcoCalendar  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IcoHash      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>;
const IcoMachine   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
const IcoStore     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const IcoInfo      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const IcoRules     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
const IcoChevDown  = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M6 8L10 12L14 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoChevRight = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M8 6L12 10L8 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoPlusCircle= () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>;
const IcoTrash     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/></svg>;
const IcoPlug      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 2v6M15 2v6M6 8h12l-1 6a5 5 0 0 1-10 0z"/><path d="M12 18v4"/></svg>;

/* ════════════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════════════ */
export default function EventDetail({ event, onClose, onSave, onDelete, getEventDetaills, getEvents, branchOfficesData, setBranchOfficesData, setSelectedEvent }) {

  /* ════════════════════════════════════
     LÓGICA ORIGINAL — 100% INTACTA
  ════════════════════════════════════ */
  const navigate = useNavigate();
  const formatDateTime = (date, isEnd = false) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    const pad = (n) => n.toString().padStart(2, '0');
    if (isEnd) return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 23:59:59`;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const [editing, setEditing]     = useState(false);
  const [form, setForm]           = useState({ ...event, branchMachines: { ...(event.branchMachines || {}) } });
  const [databranchesMachines, setDataBranchesMachines] = useState([]);
  const [hovered, setHovered]     = useState(null);
  const [pm, setPm]               = useState(event.start.getMonth());
  const [py, setPy]               = useState(event.start.getFullYear());
  const { w } = useWindowSize();
  const compact = w < 640;
  const [newBranches, setNewBranches]         = useState([]);
  const [addBranchSuccess, setAddBranchSuccess] = useState(false); // éxito al agregar sucursal
  const [openBranch, setOpenBranch]           = useState(null);
  const [openMachineAccordion, setOpenMachineAccordion] = useState(null);
  const [machinesByBranch, setMachinesByBranch]         = useState({});
  const [machinesAllByBranch, setMachinesAllByBranch]   = useState({});
  const [eventHandle, setEventHandle] = useState(0);
  const handleSave = () => { onSave(form); setEditing(false); };
  const [nameEdit, setNameEdit]           = useState(event.name);
  const [descriptionEdit, setDescriptionEdit] = useState(event.description);
  const [typeEdit, setTypeEdit]           = useState("event");
  const [newDate, setNewDate]             = useState({ start: event.start, end: event.end });

  const getMachines = async () => {
    let token = localStorage.getItem('x-access-token');
    let allMachines = [];
    const requests = form.branches.map(async (branchoffice_id) => {
      try {
        const machines = await MachinesFetchAPI.getMachinesByBranchOffice(branchoffice_id, token);
        const formatdatamachine = machines.data.data.map((m) => ({ id: m.machine_per_branch_id, name: m.machine_name, color: getRandomColor() }));
        allMachines = allMachines.concat(formatdatamachine);
      } catch (err) { console.log(err); }
    });
    await Promise.all(requests);
    setDataBranchesMachines(allMachines);
    return allMachines;
  };

  const deleteBarnchFromEvent = async (branch) => {
    let token = localStorage.getItem('x-access-token');
    try {
      await EventsFetchAPI.deleteBranchofficeFromEvent({ "event_id": event.id, "branchoffices": [branch] }, token);
      const detail = await getEventDetaills(event, true);
      console.log(detail);
      setSelectedEvent(detail);
      setForm(prev => ({ ...detail, branches: (detail.branches || []).filter(bId => bId !== branch), branchMachines: { ...(detail.branchMachines || {}) } }));
      setOpenBranch(null);
    } catch (err) { AlertSmall({ message: 'Error al remover sucursal', icon: 'error' }); }
  };

  const updateEvent = async (state) => {
    let token = localStorage.getItem('x-access-token');
    try {
      let data = { name: nameEdit, description: descriptionEdit };
      console.log(state);
      if (state !== undefined) data.state = state;
      const response = await EventsFetchAPI.updateEvent(event.id, data, token);
      await getEvents();
      const detail = await getEventDetaills(event, true);
      getEvents();
      setSelectedEvent(detail);
      if (state) setForm({ ...detail, state: state });
      else setForm({ ...detail, name: nameEdit, description: descriptionEdit });
      setEditing(false);
    } catch (err) { console.log(err); }
  };

  const updateDateEvent = async () => {
    try {
      let token = localStorage.getItem('x-access-token');
      let data = { initialDate: formatDateTime(newDate.start), maxDate: formatDateTime(newDate.end, true) };
      console.log(data);
      const response = await EventsFetchAPI.updateDateEvent(event.date_id, data, token);
      getEvents();
      setForm(prev => ({ ...prev, start: newDate.start, end: newDate.end }));
      AlertSmall({ message: 'Fechas del evento actualizadas correctamente', icon: 'success' });
      setEditing(false);
      setTypeEdit("event");
      setEventHandle(1);
    } catch (err) { AlertSmall({ message: 'Error al actualizar fechas del evento', icon: 'error' }); }
  };

  const asignBranchToEvent = async () => {
    let startTime = Date.now();
    try {
      let token = localStorage.getItem('x-access-token');
      let data = { "event_id": event.id, "branchoffices": newBranches };
      const res = await EventsFetchAPI.assignBranchofficestoEvent(data, token);
      AlertSmall({ message: 'Sucursal asignada al evento correctamente', icon: 'success' });
      getEvents(event);
      const detail = await getEventDetaills(event, true);
      setSelectedEvent(detail);
      setForm({ ...detail, branchMachines: { ...(detail.branchMachines || {}) } });
      setNewBranches([]);
      setAddBranchSuccess(true); // muestra mensaje de éxito en el acordeón
    } catch (err) { AlertSmall({ message: 'Error al asignar sucursal al evento', icon: 'error' }); }
  };

  useEffect(() => {
    console.log("Evento seleccionado:", event);
    if (event) getMachines();
  }, [event]);

  useEffect(() => { setEventHandle(0); }, [eventHandle]);
  /* ════════════════════════════════════
     FIN LÓGICA ORIGINAL
  ════════════════════════════════════ */

  /* ── TAB STATE (nuevo) ── */
  const [activeTab, setActiveTab] = useState(1); // 1=Info, 2=Sucursales

  /* Cambiar tab cancela cualquier edición en curso */
  const switchTab = (tab) => {
    setActiveTab(tab);
    setEditing(false);
  };

  /* handleToggle reutilizado en ambos tabs */
  const handleBranchToggle = async (bId) => {
    if (openBranch === bId) { setOpenBranch(null); return; }
    setOpenBranch(bId);
    let token = localStorage.getItem('x-access-token');
    try {
      const machines = await MachinesFetchAPI.getMachinesByBranchOffice(bId, token);
      const allowedIds = event.branchMachines && event.branchMachines[bId] ? event.branchMachines[bId] : [];
      setMachinesByBranch(prev => ({ ...prev, [bId]: machines.data.data.filter(m => allowedIds.includes(m.machine_per_branch_id)) }));
      setMachinesAllByBranch(prev => ({ ...prev, [bId]: machines.data.data }));
    } catch (err) {
      setMachinesByBranch(prev => ({ ...prev, [bId]: [] }));
      setMachinesAllByBranch(prev => ({ ...prev, [bId]: [] }));
    }
  };

  /* ════════════════════════════════════════════════════════
     RENDER — estructura 3 zonas
  ════════════════════════════════════════════════════════ */
  return (
    <div className="evd-panel">

      {/* ══════════════════════════════════════
          ZONA 1A — HEADER fijo
      ══════════════════════════════════════ */}
      <div className="ec-detail-header evd-panel-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="evd-panel-eyebrow">
            {editing
              ? (typeEdit === 'date' ? 'Editando fechas' : 'Editando información')
              : 'Detalle del evento'}
          </p>
          <h2 className="evd-panel-title">{form.name}</h2>
        </div>
        <button onClick={onClose} className="ec-btn-close evd-close">✕</button>
      </div>

      {/* ══════════════════════════════════════
          ZONA 1B — TABS fijos
      ══════════════════════════════════════ */}
      <div className="evd-panel-tabs">
        <button
          className={`evd-tab ${activeTab === 1 ? 'active' : ''}`}
          onClick={() => switchTab(1)}
        >
          <IcoInfo /> Información del evento
        </button>
        <button
          className={`evd-tab ${activeTab === 2 ? 'active' : ''}`}
          onClick={() => switchTab(2)}
        >
          <IcoStore /> Sucursales
          <span className="evd-tab-badge">{(form.branches || []).length}</span>
        </button>
        <button
          className={`evd-tab ${activeTab === 3 ? 'active' : ''}`}
          onClick={() => switchTab(3)}
        >
          <IcoPlug /> Integraciones
        </button>
      </div>

      {/* ══════════════════════════════════════
          ZONA 2 — BODY scrollable
      ══════════════════════════════════════ */}
      <div className="evd-panel-body">

        {/* ─────── TAB 1: INFORMACIÓN ─────── */}
        {activeTab === 1 && (
          <>
            {/* VISTA — info rows */}
            {!editing && (
              <>
                {/* Nombre */}
                <div className="ec-detail-branch-name evd-ir" onClick={() => { setEditing(true); setTypeEdit("event"); }}>
                  <div className="evd-ir-content">
                    <span className="evd-ir-label">Nombre del evento</span>
                    <span className="evd-ir-value">{form.name}</span>
                  </div>
                  <span className="evd-ir-pencil"><IcoEdit /></span>
                </div>

                {/* Fechas */}
                <div className="ec-detail-branch-name evd-ir" onClick={() => { setEditing(true); setTypeEdit("date"); }}>
                  <div className="evd-ir-content">
                    <span className="evd-ir-label">Fechas del evento</span>
                    <span className="evd-ir-value">{formatDate(form.start)} → {formatDate(form.end)}</span>
                  </div>
                  <span className="evd-ir-pencil"><IcoCalendar /></span>
                </div>

                {/* Código */}
                <div className="ec-detail-branch-name evd-ir evd-static">
                  <div className="evd-ir-content">
                    <span className="evd-ir-label">Código de evento</span>
                    <span className="evd-ir-value">{form.code}</span>
                  </div>
                  <span className="evd-ir-pencil" style={{ opacity: 0 }}><IcoHash /></span>
                </div>

                {/* Descripción */}
                {form.description && (
                  <div className="ec-detail-branch-name evd-ir" onClick={() => { setEditing(true); setTypeEdit("event"); }}>
                    <div className="evd-ir-content">
                      <span className="evd-ir-label">Descripción</span>
                      <span className="evd-ir-value" style={{ fontWeight: 500, color: '#4B5563', whiteSpace: 'normal' }}>
                        {form.description}
                      </span>
                    </div>
                    <span className="evd-ir-pencil"><IcoEdit /></span>
                  </div>
                )}

                {/* Máquina ecommerce */}
                <div className="ec-detail-branch-name evd-ir" style={{ alignItems: 'flex-start' }} onClick={() => { switchTab(2); }}>
                  <div className="evd-ir-content">
                    <span className="evd-ir-label">Máquina ecommerce asignada</span>
                    <div className="ec-form-group evd-chip-row" style={{ margin: 0 }}>
                      {Object.entries(form.branchMachines || {}).map(([bId, machineIds]) => {
                        const branch = branchOfficesData.find(b => b.id === bId);
                        if (!branch) return null;
                        return machineIds.map(mId => {
                          const m = branch.machines && branch.machines.find(x => x.id === mId || x.machine_per_branch_id === mId);
                          if (m && (m.type === "Smartpay Ecommerce" || m.name === "Smartpay Ecommerce")) {
                            return <span key={mId} className="evd-chip" style={{ background: branch.color + '14', color: branch.color }}>{branch.name} — {m.name}</span>;
                          }
                          return null;
                        }).filter(Boolean);
                      })}
                      {Object.entries(form.branchMachines || {}).every(([bId, machineIds]) => {
                        const branch = branchOfficesData.find(b => b.id === bId);
                        if (!branch) return true;
                        return machineIds.every(mId => {
                          const m = branch.machines && branch.machines.find(x => x.id === mId || x.machine_per_branch_id === mId);
                          return !(m && (m.type === "Smartpay Ecommerce" || m.name === "Smartpay Ecommerce"));
                        });
                      }) && <span className="evd-empty-hint">Sin máquinas ecommerce asignadas — ir a Sucursales →</span>}
                    </div>
                  </div>
                  <span className="evd-ir-pencil"><IcoMachine /></span>
                </div>

                {/* Notas */}
                {form.notes && (
                  <div style={{ marginTop: '.25rem' }}>
                    <span className="evd-section-label">Notas</span>
                    <p style={{ fontSize: '.83rem', color: '#374151', margin: 0, lineHeight: 1.6 }}>{form.notes}</p>
                  </div>
                )}
              </>
            )}

            {/* EDICIÓN EVENTO (nombre + descripción) */}
            {editing && typeEdit === 'event' && (
              <>
                <div>
                  <label className="evd-edit-label">Nombre del evento</label>
                  <input value={nameEdit} onChange={e => setNameEdit(e.target.value)} className="ec-input evd-input" />
                </div>
                <div>
                  <label className="evd-edit-label">Descripción</label>
                  <textarea value={descriptionEdit} onChange={e => setDescriptionEdit(e.target.value)} className="ec-textarea evd-textarea" />
                </div>
              </>
            )}

            {/* EDICIÓN FECHAS */}
            {editing && typeEdit === 'date' && (
              <>
                <div>
                  <label className="evd-edit-label">Rango de fechas</label>
                  <div className="ec-range-wrap evd-range-wrap">
                    <RangeCalendar
                      start={newDate.start}
                      end={newDate.end}
                      onRangeChange={(s, e) => setNewDate({ start: s, end: e })}
                      hovered={hovered}
                      setHovered={setHovered}
                      pickerMonth={pm}
                      pickerYear={py}
                      setPickerMonth={setPm}
                      setPickerYear={setPy}
                      compact={compact}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ─────── TAB 2: SUCURSALES ─────── */}
        {activeTab === 2 && (
          <>
            {/* agregar nueva sucursal */}
            <div
              className={`ec-detail-branch evd-accordion${openBranch === 'add' ? ' ec-detail-branch--active' : ''}`}
              style={{ border: '1px solid #e5e7eb', boxShadow: openBranch === 'add' ? '0 2px 8px #94a3b820' : 'none', background: openBranch === 'add' ? '#f3f4f6' : '#fff' }}
            >
              <div
                className="ec-detail-branch-name evd-accordion-header"
                style={{ cursor: 'pointer', color: '#1B3A6B', borderBottom: openBranch === 'add' ? '1px solid #e5e7eb' : 'none', background: openBranch === 'add' ? 'rgba(27,58,107,.06)' : 'rgba(27,58,107,.03)', borderRadius: openBranch === 'add' ? '12px 12px 0 0' : '12px' }}
                onClick={() => setOpenBranch(openBranch === 'add' ? null : 'add')}
              >
                <span style={{ marginRight: 8, display: 'flex' }}><IcoPlusCircle /></span>
                <span style={{ flex: 1, fontSize: '.84rem', fontWeight: 700 }}>Agregar nueva sucursal</span>
                <span style={{ display: 'flex', alignItems: 'center', color: openBranch === 'add' ? '#1B3A6B' : '#b0b8c4' }}>
                  {openBranch === 'add' ? <IcoChevDown /> : <IcoChevRight />}
                </span>
              </div>
              {openBranch === 'add' && (
                <div className="ec-detail-branch-machines evd-accordion-body">
                  {/* ÉXITO — sucursal agregada */}
                  {addBranchSuccess ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.75rem', padding: '.5rem 0' }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: 'rgba(22,163,74,.1)', color: '#16A34A',
                        display: 'grid', placeItems: 'center', fontSize: 22,
                      }}>✓</div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: '.87rem', color: '#111827', textAlign: 'center' }}>
                        ¡Sucursal agregada correctamente!
                      </p>
                      <p style={{ margin: 0, fontSize: '.76rem', color: '#9CA3AF', textAlign: 'center' }}>
                        Ya aparece en la lista de sucursales de este evento.
                      </p>
                      <button
                        className="ec-btn-primary evd-btn-primary"
                        style={{ flex: 'none', width: '100%', marginTop: '.25rem' }}
                        onClick={() => { setOpenBranch(null); setAddBranchSuccess(false); }}
                      >
                        ← Volver a sucursales
                      </button>
                    </div>
                  ) : (
                    /* SELECCIÓN — lista de sucursales disponibles */
                    <>
                      {branchOfficesData.filter(b => !(form.branches || []).includes(b.id)).map(b => {
                        const sel = newBranches.includes(b.id);
                        return (
                          <button
                            key={b.id}
                            onClick={() => { const updated = sel ? newBranches.filter(x => x !== b.id) : [...newBranches, b.id]; setNewBranches(updated); }}
                            className="ec-branch-option evd-branch-option"
                            style={sel ? { borderColor: b.color, background: b.color + '06' } : {}}
                          >
                            <div className={`ec-branch-icon evd-branch-icon${sel ? '' : ' ec-branch-icon--inactive evd-branch-icon--inactive'}`} style={sel ? { background: b.color + '18', color: b.color } : {}}>{b.icon}</div>
                            <div>
                              <div className={`ec-branch-name evd-branch-name${sel ? '' : ' ec-branch-name--inactive evd-branch-name--inactive'}`} style={sel ? { color: b.color } : {}}>{b.name}</div>
                              <div className="ec-branch-address evd-branch-address">{b.address}</div>
                            </div>
                            {sel && <span className="ec-branch-check evd-branch-check">✓</span>}
                          </button>
                        );
                      })}
                      {branchOfficesData.filter(b => !(form.branches || []).includes(b.id)).length === 0 && (
                        <p style={{ fontSize: '.77rem', color: '#9CA3AF', margin: 0 }}>Todas las sucursales ya están asignadas.</p>
                      )}
                      <button
                        className="ec-btn-primary evd-btn-primary"
                        style={{ marginTop: 12, alignSelf: 'flex-end', flex: 'none' }}
                        disabled={newBranches.length === 0}
                        onClick={() => asignBranchToEvent()}
                      >
                        Agregar sucursal{newBranches.length > 1 ? 'es' : ''} seleccionada{newBranches.length > 1 ? 's' : ''}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* sucursales asignadas — ocultas mientras se agrega una nueva */}
            {openBranch !== 'add' && (!form.branches || form.branches.length === 0) && (
              <p style={{ fontSize: '.77rem', color: '#9CA3AF', textAlign: 'center', margin: '.5rem 0' }}>
                Sin sucursales asignadas. Usa el botón de arriba para agregar.
              </p>
            )}

            {openBranch !== 'add' && form.branches && form.branches.map((bId) => {
              const branch = branchOfficesData.find(b => b.id === bId);
              if (!branch) return null;
              return (
                <div
                  key={bId}
                  className={`ec-detail-branch evd-accordion${openBranch === bId ? ' ec-detail-branch--active' : ''}`}
                  style={{ border: '1px solid #e5e7eb', boxShadow: openBranch === bId ? '0 2px 8px #94a3b820' : 'none', background: openBranch === bId ? branch.color + '08' : '#fff' }}
                >
                  <div
                    className="ec-detail-branch-name evd-accordion-header"
                    style={{ cursor: 'pointer', color: branch.color, borderBottom: openBranch === bId ? `1px solid ${branch.color}30` : 'none', background: openBranch === bId ? branch.color + '10' : 'transparent' }}
                    onClick={() => handleBranchToggle(bId)}
                  >
                    <span style={{ marginRight: 12 }}>{branch.icon}</span>
                    <span style={{ flex: 1 }}>{branch.name}</span>
                    <span style={{ display: 'flex', alignItems: 'center', color: openBranch === bId ? branch.color : '#b0b8c4' }}>
                      {openBranch === bId ? <IcoChevDown /> : <IcoChevRight />}
                    </span>
                  </div>

                  {openBranch === bId && (
                    <div className="ec-detail-branch-machines evd-accordion-body">
                      {/* máquinas asignadas */}
                      {machinesByBranch[bId] && machinesByBranch[bId].length === 0 && (
                        <span className="evd-empty-hint">Sin máquinas asignadas</span>
                      )}
                      {machinesByBranch[bId] && machinesByBranch[bId].map(m => (
                        <div key={m.machine_per_branch_id} className="ec-machine-badge--detail evd-machine-badge"
                          style={{ background: branch.color + '14', color: branch.color, border: `1px solid ${branch.color}30`, justifyContent: 'space-between' }}>
                          <span>{m.machine_name}</span>
                          <button
                            className="evd-machine-badge-del"
                            style={{ color: branch.color }}
                            title="Eliminar máquina"
                            onClick={async () => {
                              let token = localStorage.getItem('x-access-token');
                              try {
                                await EventsFetchAPI.deleteMachineFromEvent({ event_date_id: event.date_id, machines: [m.machine_per_branch_id] }, token);
                                const detail = await getEventDetaills(event, true);
                                setSelectedEvent(detail);
                                setForm({ ...detail, branchMachines: { ...(detail.branchMachines || {}) } });
                                const allowedIds = detail.branchMachines && detail.branchMachines[bId] ? detail.branchMachines[bId] : [];
                                setMachinesByBranch(prev => ({ ...prev, [bId]: machinesAllByBranch[bId]?.filter(mach => allowedIds.includes(mach.machine_per_branch_id)) || [] }));
                              } catch (err) { AlertSmall({ message: 'Error al eliminar máquina', icon: 'error' }); }
                            }}
                          ><IcoTrash /></button>
                        </div>
                      ))}

                      {/* acordeón agregar máquinas */}
                      <div
                        className={`ec-detail-branch evd-accordion${openMachineAccordion === bId ? ' ec-detail-branch--active' : ''}`}
                        style={{ border: '1px solid #e5e7eb', marginTop: 8, boxShadow: openMachineAccordion === bId ? '0 2px 8px #94a3b820' : 'none', background: openMachineAccordion === bId ? '#f3f4f6' : '#fff' }}
                      >
                        <div
                          className="ec-detail-branch-name evd-accordion-header"
                          style={{ cursor: 'pointer', color: '#1a1a2e', borderBottom: openMachineAccordion === bId ? '1px solid #e5e7eb' : 'none', background: openMachineAccordion === bId ? '#f3f4f6' : 'transparent' }}
                          onClick={() => setOpenMachineAccordion(openMachineAccordion === bId ? null : bId)}
                        >
                          <span style={{ marginRight: 8, display: 'flex' }}><IcoPlusCircle /></span>
                          <span style={{ flex: 1 }}>Agregar máquinas</span>
                          <span style={{ display: 'flex', alignItems: 'center', color: openMachineAccordion === bId ? '#1a1a2e' : '#b0b8c4' }}>
                            {openMachineAccordion === bId ? <IcoChevDown /> : <IcoChevRight />}
                          </span>
                        </div>
                        {openMachineAccordion === bId && (
                          <div className="ec-detail-branch-machines evd-accordion-body">
                            {machinesAllByBranch[bId] && machinesAllByBranch[bId]
                              .filter(m => !machinesByBranch[bId]?.some(a => a.machine_per_branch_id === m.machine_per_branch_id))
                              .map(m => (
                                (m.name === "Smart pos" || m.name === "Smart Pay") && (
                                  <button
                                    key={m.machine_per_branch_id}
                                    className="ec-machine-badge--add evd-machine-add"
                                    onClick={async () => {
                                      let token = localStorage.getItem('x-access-token');
                                      try {
                                        let data = { "event_date_id": event.date_id, "machines": [m.machine_per_branch_id] };
                                        const res = await EventsFetchAPI.assignMachinestoEvent(data, token);
                                        const detail = await getEventDetaills(event, true);
                                        setSelectedEvent(detail);
                                        setForm({ ...detail, branchMachines: { ...(detail.branchMachines || {}) } });
                                        const allowedIds = detail.branchMachines && detail.branchMachines[bId] ? detail.branchMachines[bId] : [];
                                        setMachinesByBranch(prev => ({ ...prev, [bId]: machinesAllByBranch[bId]?.filter(mach => allowedIds.includes(mach.machine_per_branch_id)) || [] }));
                                      } catch (err) { AlertSmall({ message: 'Error al agregar máquina', icon: 'error' }); }
                                    }}
                                  >+ {m.machine_name}</button>
                                )
                              ))}
                          </div>
                        )}
                      </div>

                      {/* quitar sucursal */}
                      <button
                        className="ec-btn-danger evd-btn-danger"
                        style={{ marginTop: 12, alignSelf: 'flex-end' }}
                        onClick={async () => deleteBarnchFromEvent(bId)}
                      ><IcoTrash /> Quitar sucursal del evento</button>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* ─────── TAB 3: INTEGRACIONES ─────── */}
        {activeTab === 3 && (
          <MomentusEventPanel eventId={event.id} />
        )}

      </div>{/* /panel-body */}

      {/* ══════════════════════════════════════
          ZONA 3 — FOOTER fijo (botones siempre visibles)
      ══════════════════════════════════════ */}
      <div className="ec-detail-actions evd-panel-footer">

        {/* Tab 1 — vista */}
        {activeTab === 1 && !editing && (
          <>
            <button onClick={() => navigate("/rules/event/" + event.id)} className="ec-btn-primary evd-btn-primary">
              <IcoRules /> Reglas
            </button>
            {form.state === 1
              ? <button className="ec-btn-primary evd-btn-primary evd-pill-active" onClick={() => updateEvent(0)}>● Activo</button>
              : <button className="ec-btn-primary evd-btn-primary evd-pill-inactive" onClick={() => updateEvent(1)}>● Inactivo</button>}
          </>
        )}

        {/* Tab 1 — editando nombre/descripción */}
        {activeTab === 1 && editing && typeEdit === 'event' && (
          <>
            <button className="ec-btn-secondary evd-btn-secondary" onClick={() => setEditing(false)}>Cancelar</button>
            <button className="ec-btn-primary evd-btn-primary" onClick={() => updateEvent()}>Guardar cambios</button>
          </>
        )}

        {/* Tab 1 — editando fechas */}
        {activeTab === 1 && editing && typeEdit === 'date' && (
          <>
            <button className="ec-btn-secondary evd-btn-secondary" onClick={() => setEditing(false)}>Cancelar</button>
            <button className="ec-btn-primary evd-btn-primary" onClick={() => updateDateEvent()}>Actualizar fecha</button>
          </>
        )}

        {/* Tab 2 — sucursales */}
        {activeTab === 2 && (
          <button className="ec-btn-secondary evd-btn-secondary" style={{ flex: 'none' }} onClick={onClose}>Cerrar</button>
        )}

      </div>

      {/* imports no usados directamente — preservados */}
      {void [BRANCHES, MACHINES, BranchMachineAssign, databranchesMachines, handleSave, typeEdit]}
    </div>
  );
}